// appleMapKit.js - Apple MapKit JS loader, initialization and shared helpers.
// Replaces the Google Maps JS API for all map RENDERING.
// (Google Places autocomplete + geocoding are intentionally kept on Google.)

const MAPKIT_SRC = 'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js';

let mapkitPromise = null;

/**
 * Fetch a fresh MapKit JWT from our backend.
 * The token is server-minted so it works from any origin
 * (travy.buzz, localhost, capacitor://localhost).
 */
async function fetchMapKitToken() {
  const base = process.env.REACT_APP_API_URL || '/api';
  const res = await fetch(`${base}/mapkit/token`);
  if (!res.ok) throw new Error(`MapKit token request failed: ${res.status}`);
  const data = await res.json();
  if (!data.token) throw new Error('MapKit token missing in response');
  return data.token;
}

/**
 * Load mapkit.js (once) and initialize it (once).
 * Returns a promise resolving to the global `mapkit` object.
 */
export function loadMapKit() {
  if (mapkitPromise) return mapkitPromise;

  mapkitPromise = new Promise((resolve, reject) => {
    const initMapKit = () => {
      try {
        window.mapkit.init({
          authorizationCallback: (done) => {
            fetchMapKitToken()
              .then(done)
              .catch((err) => {
                console.error('[MapKit] token fetch failed:', err);
                // Passing an invalid token surfaces a clear mapkit error
                done('');
              });
          },
          language: undefined, // let MapKit follow the browser/app language
        });
        resolve(window.mapkit);
      } catch (err) {
        reject(err);
      }
    };

    if (window.mapkit && window.mapkit.init) {
      initMapKit();
      return;
    }

    const existing = document.querySelector(`script[src="${MAPKIT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', initMapKit);
      existing.addEventListener('error', () => reject(new Error('mapkit.js failed to load')));
      return;
    }

    const script = document.createElement('script');
    script.src = MAPKIT_SRC;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.addEventListener('load', initMapKit);
    script.addEventListener('error', () => reject(new Error('mapkit.js failed to load')));
    document.head.appendChild(script);
  });

  return mapkitPromise;
}

// ---------------------------------------------------------------------------
// Coordinate / region / zoom helpers
// ---------------------------------------------------------------------------

export function toCoordinate(lat, lng) {
  return new window.mapkit.Coordinate(Number(lat), Number(lng));
}

/**
 * Convert an integer "Google-style" zoom level to a mapkit.CoordinateRegion.
 * Uses Web Mercator math: lngDelta = 360 * widthPx / (256 * 2^zoom).
 */
export function zoomToRegion(center, zoom, widthPx = 1024, heightPx = 768) {
  const lngDelta = (360 * widthPx) / (256 * Math.pow(2, zoom));
  const latRad = (center.lat * Math.PI) / 180;
  const latDelta = lngDelta * (heightPx / widthPx) * Math.cos(latRad);
  return new window.mapkit.CoordinateRegion(
    toCoordinate(center.lat, center.lng),
    new window.mapkit.CoordinateSpan(latDelta, lngDelta)
  );
}

/** Approximate Google-style zoom level from the map's current region. */
export function regionToZoom(map) {
  const widthPx = map.element ? map.element.clientWidth || 1024 : 1024;
  const lngDelta = map.region.span.longitudeDelta || 0.0001;
  const zoom = Math.log2((360 * widthPx) / (256 * lngDelta));
  return Math.max(1, Math.min(21, Math.round(zoom * 100) / 100));
}

/** Extract {ne, sw} bounds (google-map-react shape) from the map's region. */
export function mapToBounds(map) {
  const c = map.region.center;
  const s = map.region.span;
  return {
    ne: { lat: c.latitude + s.latitudeDelta / 2, lng: c.longitude + s.longitudeDelta / 2 },
    sw: { lat: c.latitude - s.latitudeDelta / 2, lng: c.longitude - s.longitudeDelta / 2 },
  };
}

/** Extract plain {lat, lng} center from the map. */
export function mapToCenter(map) {
  return { lat: map.region.center.latitude, lng: map.region.center.longitude };
}

/** Pan the map to a new center, keeping the current span. */
export function panTo(map, lat, lng, animated = true) {
  map.setCenterAnimated(toCoordinate(lat, lng), animated);
}

/** Set a Google-style zoom level, keeping the current center. */
export function setZoom(map, zoom, animated = true) {
  const widthPx = map.element ? map.element.clientWidth || 1024 : 1024;
  const heightPx = map.element ? map.element.clientHeight || 768 : 768;
  const center = mapToCenter(map);
  map.setRegionAnimated(zoomToRegion(center, zoom, widthPx, heightPx), animated);
}

// ---------------------------------------------------------------------------
// Custom DOM annotations (replacement for google.maps.OverlayView)
// ---------------------------------------------------------------------------

/**
 * Create a custom DOM annotation. `el` is any HTML element you control.
 * anchorOffsetY: negative values raise the element (e.g. pin tips).
 * Returns the mapkit.Annotation. Add/remove via map.addAnnotation/removeAnnotation.
 */
export function createDomAnnotation(position, el, { anchorOffsetY = 0, anchorOffsetX = 0 } = {}) {
  const annotation = new window.mapkit.Annotation(
    toCoordinate(position.lat, position.lng),
    () => el,
    {
      anchorOffset: new DOMPoint(anchorOffsetX, anchorOffsetY),
      // Always keep custom annotations visible; our overlay classes manage
      // their own show/hide via element styles.
      displayPriority: 1000,
      animates: false,
    }
  );
  return annotation;
}

/**
 * Raise (or lower) a custom annotation above/below its siblings.
 * MapKit wraps the factory element in its own absolutely-positioned
 * containers, so the z-index must be applied to the wrapper that sits
 * among the other annotations, not just to our own element.
 */
export function setAnnotationZIndex(el, z) {
  let node = el;
  let depth = 0;
  while (node && depth < 4) {
    node.style.zIndex = String(z);
    const parent = node.parentElement;
    // Stop once this node sits among sibling annotations (the layer container)
    if (!parent || parent.children.length > 1) break;
    node = parent;
    depth++;
  }
}

/**
 * Mark that an annotation consumed a tap, so the map-level single-tap
 * handler (used for "click background to clear selection") ignores it.
 */
export function markAnnotationTap(map) {
  if (map) map.__lastAnnotationTapAt = Date.now();
}

export function wasAnnotationTapRecent(map, windowMs = 400) {
  return map && map.__lastAnnotationTapAt && Date.now() - map.__lastAnnotationTapAt < windowMs;
}
